import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1630920501459-f3e99320c4a5?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

export default function Login() {
  const { login } = useAuth();
  const [mode, setMode] = useState("pin");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isPin = mode === "pin";

  const switchMode = () => {
    setMode(isPin ? "password" : "pin");
    setIdentifier("");
    setPassword("");
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isPin && (!/^\d{10}$/.test(identifier.replace(/\D/g, "").slice(-10)) || !/^\d{4}$/.test(password))) {
      return setError("Enter your 10-digit mobile number and 4-digit PIN");
    }
    setBusy(true);
    setError("");
    try {
      await login(identifier, password);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background md:flex-row">
      <div className="relative h-48 overflow-hidden md:h-auto md:w-1/2">
        <img src={HERO} alt="Folded fabric stacks" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10" />
        <div className="absolute bottom-4 left-4 text-white">
          <p className="mono-label opacity-80">Bengaluru · Purchase to showroom</p>
          <p className="font-heading text-2xl font-bold" data-testid="login-app-name">Man &amp; Moda ERP</p>
        </div>
      </div>
      <div className="flex flex-1 items-center px-5 py-8 md:px-12">
        <form onSubmit={submit} className="rise mx-auto w-full max-w-sm space-y-5" data-testid="login-form">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Sign in</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {isPin ? "Use your mobile number and 4-digit PIN. You'll see the screens for your role." : "Sign in with your email (or mobile) and password."}
            </p>
          </div>
          <div>
            <label className="label" htmlFor="identifier">{isPin ? "Mobile number" : "Email or mobile number"}</label>
            <input id="identifier" className={`field ${isPin ? "mono" : ""}`} autoComplete="username" value={identifier} required
              type={isPin ? "tel" : "text"} inputMode={isPin ? "numeric" : "email"}
              onChange={(e) => setIdentifier(e.target.value)} placeholder={isPin ? "98xxxxxxxx" : "name@manandmoda.com"} data-testid="login-identifier-input" />
          </div>
          <div>
            <label className="label" htmlFor="password">{isPin ? "PIN" : "Password"}</label>
            {isPin ? (
              <input id="password" type="password" className="field mono text-center text-2xl tracking-[0.75em]" inputMode="numeric" maxLength={4}
                autoComplete="current-password" value={password} required
                onChange={(e) => setPassword(e.target.value.replace(/\D/g, ""))} data-testid="login-pin-input" />
            ) : (
              <input id="password" type="password" className="field" autoComplete="current-password" value={password} required
                onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
            )}
          </div>
          {error && <p className="text-sm font-medium text-destructive" data-testid="login-error">{error}</p>}
          <button className="btn btn-primary w-full" disabled={busy} data-testid="login-submit-btn">
            {busy && <Loader2 size={18} className="animate-spin" />} Sign in
          </button>
          <button type="button" className="w-full text-sm font-semibold text-brand underline-offset-4 hover:underline" onClick={switchMode} data-testid="login-mode-toggle">
            {isPin ? "Use email & password instead" : "Use mobile & PIN instead"}
          </button>
          <p className="text-xs text-muted-foreground">No account or forgot your PIN? Ask your admin.</p>
        </form>
      </div>
    </div>
  );
}
